import { FC } from 'react';
import Typography from 'components/shared/generic/typography/presentational/Typography';
import Button from 'components/shared/generic/button/presentational/Button';
import useTranslate from 'lib/languages/useTranslate';
import { useRestrictedAction } from '../../../../../hooks';
import { ReactComponent as LockIcon } from 'lib/_content/images/icons/lock.svg';
import { PersonalDetailSection } from 'lib/type';

interface PersonalDetailsProps {
    data: {
        title: string;
        forename: string;
        surname: string;
        buildingNo?: string;
        buildingName?: string;
        streetAddress?: string;
        town: string;
        postcode: string;
        country: string;
        countryCode?: string;
        email: string;
        diallingCode: string;
        mobile: string;
    };
    editClick: (editPart: PersonalDetailSection) => void;
}

const PersonalDetails: FC<PersonalDetailsProps> = ({ data, editClick }) => {
    const {
        title,
        forename,
        surname,
        buildingNo,
        buildingName,
        streetAddress,
        town,
        postcode,
        country,
        countryCode,
        email,
        diallingCode,
        mobile,
    } = data;
    const {
        handleRestrictedAction,
        isAccountRestricted,
    } = useRestrictedAction();

    const translate = useTranslate('personalDetails');
    const editText = translate('edit');

    const addressLine1 = [buildingName, buildingNo, streetAddress]
        .filter(field => field && field.trim() !== '')
        .join(' ');

    const renderLockIcon = () =>
        isAccountRestricted && (
            <LockIcon
                style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '8px',
                }}
            />
        );

    return (
        <>
            <div className="personal-details">
                <Typography tag="h1">{translate('name')}</Typography>
                <Typography>{`${title} ${forename} ${surname}`}</Typography>
            </div>
            <div className="personal-details">
                <Typography tag="h1">{translate('email')}</Typography>
                <Typography>{email}</Typography>
            </div>
            <div className="personal-details spacing-bottom d-flex-row-between">
                <div>
                    <Typography tag="h1">{translate('mobile')}</Typography>
                    <Typography>
                        {diallingCode}
                        {mobile}
                    </Typography>
                </div>
                <Button
                    icon={renderLockIcon()}
                    onClick={() =>
                        handleRestrictedAction(() =>
                            editClick(PersonalDetailSection.CONTACT),
                        )
                    }
                >
                    {editText}
                </Button>
            </div>
            <div className="personal-details spacing-bottom d-flex-row-between">
                <div>
                    <Typography tag="h1">{translate('address')}</Typography>
                    {addressLine1 && <Typography>{addressLine1},</Typography>}
                    <Typography>{town},</Typography>
                    <Typography>{country},</Typography>
                    {countryCode && <Typography>{countryCode},</Typography>}
                    <Typography>{postcode}</Typography>
                </div>
                <Button
                    icon={renderLockIcon()}
                    onClick={() =>
                        handleRestrictedAction(() =>
                            editClick(PersonalDetailSection.ADDRESS),
                        )
                    }
                >
                    {editText}
                </Button>
            </div>
        </>
    );
};

export default PersonalDetails;
